import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * DocumentCategoriesReference — READ-ONLY reference page.
 *
 * Enumerates the 12 canonical document categories used by the existing durable pipeline.
 * NOT a taxonomy editor. NOT a user-managed list. Phase 1 governance decision:
 * categories remain centrally governed; renames require an approved migration.
 *
 * Source of truth: hard-coded enum in /app/backend/routes/participant_documents.py
 */

const CANONICAL_CATEGORIES = [
  { id: 'ID', label: 'Identification' , description: 'Photo ID, passport, driver licence, Medicare card and similar identification documents.' },
  { id: 'SERVICE_AGREEMENT', label: 'Service Agreement', description: 'Signed participant service agreements.' },
  { id: 'CONSENT', label: 'Consent', description: 'Signed consent forms — pairs with structured consent records elsewhere.' },
  { id: 'POLICY', label: 'Policy', description: 'Organisational policy documents applicable to the participant.' },
  { id: 'HR', label: 'HR', description: 'HR / workforce documents (rarely participant-scoped).' },
  { id: 'INCIDENT_DOC', label: 'Incident Attachment', description: 'Attachments linked to incident records — managed by Incident Management.' },
  { id: 'NDIS_PLAN', label: 'NDIS Plan', description: 'Uploaded NDIS plan documents.' },
  { id: 'BEHAVIOUR_SUPPORT_PLAN', label: 'Behaviour Support Plan', description: 'Uploaded external Behaviour Support Plan.' },
  { id: 'MEDICAL', label: 'Medical', description: 'Uploaded medical letters, reports, and clinical correspondence.' },
  { id: 'GUARDIAN_DOCUMENT', label: 'Guardian Document', description: 'Guardianship and nominee legal documents.' },
  { id: 'RISK_ASSESSMENT_DOC', label: 'Risk Assessment Document', description: 'Uploaded risk-assessment artefacts (companion to structured Risk records).' },
  { id: 'OTHER', label: 'Other', description: 'Any document that does not fit the categories above.' },
];

export function DocumentCategoriesReference() {
  return (
    <div className="space-y-3" data-testid="document-categories-tab">
      <div>
        <h3 className="text-sm font-semibold">Document Categories (canonical reference)</h3>
        <p className="text-[11px] text-muted-foreground italic">
          Centrally governed. Renaming a category requires an approved migration. Phase 1 does
          not enable user-managed category taxonomy.
        </p>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {CANONICAL_CATEGORIES.map((c) => (
          <Card key={c.id} data-testid={`document-category-${c.id}`}>
            <CardContent className="py-3">
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px] font-mono">{c.id}</Badge>
                <span className="text-sm font-medium">{c.label}</span>
              </div>
              <p className="text-xs text-muted-foreground">{c.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default DocumentCategoriesReference;
